import { Button, IconButton } from '@ads/components-react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useEffect, useRef } from 'react';

/** Assinatura dos ícones do Hero Icons. */
type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

/** Props de `ConfirmDialog`. */
export interface ConfirmDialogProps {
  /** Abre e fecha o diálogo. */
  readonly open: boolean;
  /** `danger` para ações destrutivas; `brand` para as demais. */
  readonly tone: 'danger' | 'brand';
  /** Ícone do cabeçalho. Em `danger` o padrão é o triângulo de alerta. */
  readonly icon?: HeroIcon | undefined;
  readonly title: string;
  readonly description?: string | undefined;
  /** Conteúdo entre a descrição e os botões. */
  readonly children?: ReactNode;
  readonly confirmLabel: string;
  readonly confirmIcon?: HeroIcon | undefined;
  readonly cancelLabel: string;
  /**
   * Rótulo acessível do "X". Omitido, o botão não é renderizado.
   *
   * Um ícone de fechar sem nome próprio seria anunciado igual ao botão de
   * cancelar, deixando os dois indistinguíveis para leitor de tela — melhor não
   * ter o atalho do que tê-lo mudo. Fechar segue disponível por `Escape`,
   * backdrop e pelo botão de cancelar.
   */
  readonly closeAriaLabel?: string | undefined;
  /** Request em voo: trava o fechamento e põe o botão em carregamento. */
  readonly isPending?: boolean | undefined;
  /**
   * Desabilita só o botão de confirmar, mantendo cancelar e fechar ativos.
   *
   * Diferente de `isPending`: serve ao diálogo que pede algo do operador antes de
   * poder agir — um arquivo escolhido, por exemplo — em que o botão está inerte
   * porque falta entrada, não porque há request em voo.
   */
  readonly confirmDisabled?: boolean | undefined;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

/**
 * Diálogo de confirmação sobre o `<dialog>` nativo.
 *
 * **Por que não o `ModalDialog` do ADS.** Ele é um Radix Dialog, e o modo modal
 * do Radix marca o `body` inteiro com `pointer-events: none` enquanto aberto,
 * desfazendo isso na desmontagem. Quando o diálogo é aberto de dentro de outro
 * layer (o menu de ações da linha) ou desmontado antes de fechar, a limpeza não
 * roda e o estilo fica preso: a página inteira para de responder ao clique. Não
 * havia como corrigir isso de fora — só mitigar, mantendo o componente montado e
 * varrendo o `body` depois de cada fechamento.
 *
 * O `<dialog>` com `showModal()` não tem esse problema porque não existe lock
 * global: o browser torna o resto da página inerte enquanto o diálogo está no
 * top layer e libera ao fechar, sem tocar em estilo nenhum. De brinde vêm foco
 * preso, `Escape` e backdrop nativos — que o Radix precisa emular.
 *
 * O visual segue os tokens do design system; só o mecanismo do overlay mudou.
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={target !== null}
 *   tone="danger"
 *   title="Inativar plano financeiro"
 *   confirmLabel="Inativar"
 *   cancelLabel="Voltar"
 *   closeAriaLabel="Fechar"
 *   onConfirm={handleConfirm}
 *   onClose={() => setTarget(null)}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  tone,
  icon,
  title,
  description,
  children,
  confirmLabel,
  confirmIcon,
  cancelLabel,
  closeAriaLabel,
  isPending = false,
  confirmDisabled = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  // `showModal()` é o que põe o diálogo no top layer — `open` como atributo o
  // exibiria inline, sem backdrop nem inerência. Chamar duas vezes lança, daí a
  // checagem de `open` no elemento.
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const Icon = icon ?? (tone === 'danger' ? ExclamationTriangleIcon : undefined);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <dialog
      ref={ref}
      aria-labelledby="confirm-dialog-title"
      className="m-auto w-[min(40rem,calc(100vw-2rem))] rounded-sm bg-ctx-layout-surface p-0 text-ctx-content-base shadow-level3 backdrop:bg-base-gray-900/intense"
      // `cancel` é o Escape. Cancelar o evento mantém o diálogo aberto durante a
      // request — fechar no meio deixaria o operador sem saber o que aconteceu.
      onCancel={event => {
        event.preventDefault();
        if (!isPending) onClose();
      }}
      // O `<dialog>` recebe o clique do backdrop como se fosse nele próprio: o
      // alvo só é o elemento quando o clique caiu fora do conteúdo.
      onClick={event => {
        if (event.target === ref.current && !isPending) onClose();
      }}
    >
      {/* O padding vive em cada bloco, não no container: uma régua desenhada sob
          um container com `p-xxs` pararia 24px antes da borda do diálogo, em vez
          de atravessá-lo. As verticais somam os mesmos 24px de antes entre um
          bloco e o seguinte (12 + 12), com a linha no meio do vão — o ritmo não
          muda, só ganha a divisória. */}
      <div className="flex flex-col">
        <div className="flex items-start gap-mini border-b-hairline border-ctx-layout-border px-xxs pt-xxs pb-micro">
          {Icon ? (
            <span
              className={`flex shrink-0 items-center justify-center rounded-pill p-micro ${
                tone === 'danger' ? 'bg-base-danger-100' : 'bg-base-accent-100'
              }`}
            >
              <Icon
                className={`h-xs w-xs ${
                  tone === 'danger' ? 'text-base-danger-700' : 'text-base-accent-700'
                }`}
              />
            </span>
          ) : null}

          <span className="flex flex-1 flex-col gap-quark">
            <h2 id="confirm-dialog-title" className="ts-heading-xs text-ctx-content-title">
              {title}
            </h2>
            {description ? (
              <p className="ts-paragraph-xs text-ctx-content-base">{description}</p>
            ) : null}
          </span>

          <IconButton
            icon={XMarkIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={closeAriaLabel ?? ''}
            disabled={isPending}
            onClick={onClose}
          />
        </div>

        {/* A segunda régua fica no rodapé do conteúdo, não no topo dos botões:
            assim o diálogo sem `children` — os de pura confirmação — não empilha
            as duas bordas e ganha uma linha de 2px entre título e botões. */}
        {children ? (
          <div className="flex flex-col gap-micro border-b-hairline border-ctx-layout-border px-xxs py-micro">
            {children}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-micro px-xxs py-mini">
          <Button hierarchy="secondary" size="md" disabled={isPending} onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            hierarchy={tone === 'danger' ? 'danger' : 'primary'}
            size="md"
            {...(confirmIcon ? { leadingIcon: confirmIcon } : {})}
            isLoading={isPending}
            disabled={isPending || confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
